import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import cookieManager from '../services/cookieManager';
import './CookieConsent.css';

const CookiePreferences = ({ onClose }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState({});
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    const consent = cookieManager.getConsent();
    setCookieSettings(consent.settings);
  }, []);

  const handleSettingChange = (category, value) => {
    if (category === 'necessary') return;
    setCookieSettings(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleSaveSettings = () => {
    cookieManager.saveConsent(cookieSettings, 'preferences_updated');
    if (onClose) onClose();
  };

  const handleWithdrawConsent = () => {
    cookieManager.withdrawConsent();
    const consent = cookieManager.getConsent();
    setCookieSettings(consent.settings);
  };

  const toggleDetails = (category) => {
    setShowDetails(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCookieDetails = () => cookieManager.getCookieDetails();

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-banner" style={{ maxWidth: '600px' }}>
        <div className="cookie-consent-settings">
          <h2 className="cookie-consent-title">{t('cookies.preferencesTitle', 'Cookie Preferences')}</h2>
          <p className="cookie-consent-description">
            {t('cookies.preferencesDescription', 'Manage your cookie preferences. You can change your settings at any time.')}
          </p>

          <div className="cookie-settings-list">
            {/* Necessary cookies */}
            <div className="cookie-setting-item">
              <div className="cookie-setting-header">
                <div>
                  <h3 className="cookie-setting-name">{t('cookies.necessary.title')}</h3>
                  <button 
                    onClick={() => toggleDetails('necessary')}
                    className="cookie-info-button"
                  >
                    <InformationCircleIcon className="h-3 w-3" />
                  </button>
                </div>
                <div className="cookie-setting-toggle">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled={true}
                    className="cookie-toggle-input"
                  />
                  <span className="cookie-toggle-slider cookie-toggle-disabled"></span>
                </div>
              </div>
              <p className="cookie-setting-description">{t('cookies.necessary.description')}</p>
              {showDetails.necessary && (
                <div className="cookie-details">
                  {getCookieDetails().necessary.map((cookie, index) => (
                    <div key={index} className="cookie-detail-item">
                      <strong>{cookie.name}</strong>: {cookie.purpose} 
                      <br /><small>{t('cookies.duration')}: {cookie.duration} | {t('cookies.provider')}: {cookie.provider}</small>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Analytics cookies */}
            <div className="cookie-setting-item">
              <div className="cookie-setting-header">
                <div>
                  <h3 className="cookie-setting-name">{t('cookies.analytics.title')}</h3>
                  <button 
                    onClick={() => toggleDetails('analytics')}
                    className="cookie-info-button"
                  >
                    <InformationCircleIcon className="h-3 w-3" />
                  </button>
                </div>
                <div className="cookie-setting-toggle">
                  <input
                    type="checkbox"
                    checked={cookieSettings.analytics}
                    onChange={(e) => handleSettingChange('analytics', e.target.checked)}
                    className="cookie-toggle-input"
                  />
                  <span className="cookie-toggle-slider"></span>
                </div>
              </div>
              <p className="cookie-setting-description">{t('cookies.analytics.description')}</p>
              {showDetails.analytics && (
                <div className="cookie-details">
                  {getCookieDetails().analytics.map((cookie, index) => (
                    <div key={index} className="cookie-detail-item">
                      <strong>{cookie.name}</strong>: {cookie.purpose} 
                      <br /><small>{t('cookies.duration')}: {cookie.duration} | {t('cookies.provider')}: {cookie.provider}</small>
                      {cookie.thirdParty && <><br /><small>{t('cookies.thirdParty')}: {cookie.thirdParty}</small></>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Marketing cookies */}
            <div className="cookie-setting-item">
              <div className="cookie-setting-header">
                <div>
                  <h3 className="cookie-setting-name">{t('cookies.marketing.title')}</h3>
                  <button 
                    onClick={() => toggleDetails('marketing')}
                    className="cookie-info-button"
                  >
                    <InformationCircleIcon className="h-3 w-3" />
                  </button>
                </div>
                <div className="cookie-setting-toggle">
                  <input
                    type="checkbox"
                    checked={cookieSettings.marketing}
                    onChange={(e) => handleSettingChange('marketing', e.target.checked)}
                    className="cookie-toggle-input"
                  />
                  <span className="cookie-toggle-slider"></span>
                </div>
              </div>
              <p className="cookie-setting-description">{t('cookies.marketing.description')}</p>
              {showDetails.marketing && (
                <div className="cookie-details">
                  {getCookieDetails().marketing.map((cookie, index) => (
                    <div key={index} className="cookie-detail-item">
                      <strong>{cookie.name}</strong>: {cookie.purpose} 
                      <br /><small>{t('cookies.duration')}: {cookie.duration} | {t('cookies.provider')}: {cookie.provider}</small>
                      {cookie.thirdParty && <><br /><small>{t('cookies.thirdParty')}: {cookie.thirdParty}</small></>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Functional cookies */}
            <div className="cookie-setting-item">
              <div className="cookie-setting-header">
                <div>
                  <h3 className="cookie-setting-name">{t('cookies.functional.title')}</h3>
                  <button 
                    onClick={() => toggleDetails('functional')}
                    className="cookie-info-button"
                  >
                    <InformationCircleIcon className="h-3 w-3" />
                  </button>
                </div>
                <div className="cookie-setting-toggle">
                  <input
                    type="checkbox"
                    checked={cookieSettings.functional}
                    onChange={(e) => handleSettingChange('functional', e.target.checked)}
                    className="cookie-toggle-input"
                  />
                  <span className="cookie-toggle-slider"></span>
                </div>
              </div>
              <p className="cookie-setting-description">{t('cookies.functional.description')}</p>
              {showDetails.functional && (
                <div className="cookie-details">
                  {getCookieDetails().functional.map((cookie, index) => (
                    <div key={index} className="cookie-detail-item">
                      <strong>{cookie.name}</strong>: {cookie.purpose} 
                      <br /><small>{t('cookies.duration')}: {cookie.duration} | {t('cookies.provider')}: {cookie.provider}</small>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="cookie-consent-actions">
            <button
              onClick={handleSaveSettings}
              className="cookie-consent-button cookie-consent-button-accept"
            >
              {t('cookies.saveSettings')}
            </button>
            
            <button
              onClick={handleWithdrawConsent}
              className="cookie-consent-button cookie-consent-button-reject"
            >
              <TrashIcon className="h-3 w-3 mr-1" />
              {t('cookies.withdrawConsent', 'Withdraw All')}
            </button>

            {onClose && (
              <button
                onClick={onClose}
                className="cookie-consent-button cookie-consent-button-secondary"
              >
                {t('cookies.close')}
              </button>
            )}
          </div>

          <p className="cookie-consent-legal">
            {t('cookies.gdprNotice', 'Under GDPR, you have the right to access, modify, or delete your data and withdraw consent at any time.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferences;